/**
 * The approaching-limit / limit-reached dialog.
 *
 * Design: `WSB2.0 Upgrade-Downgrade`, "Cus. on Bookings - appointments limits",
 * frames "Approaching limit modal" and "Limit reached modal". Copy differs
 * between the two states; the benefit list and the actions do not.
 *
 * Follows Gravity's own `gv-upgrade-modal` composition, so the layout is the
 * design system's rather than ours:
 *
 *   .gv-modal-content        flex row, capped at --modal-product-max-width
 *     .gv-modal-body         flex: 0 0 calc(50% - 16px) — the copy column
 *     .gv-button-group       flex: 0 0 100% — its own row beneath
 *     .gv-modal-image        absolute, right half, full height
 *
 * That covers everything this dialog needs: the two columns, the artwork crop
 * (`object-fit: cover`), the check glyphs on the benefit list, and the small
 * screen treatment, where Gravity stacks the body and moves the artwork to a
 * fixed band above it. Type sizes come from GravityDialog.
 *
 * Presentational only. Eligibility, the allowance count and the trigger rules
 * ("once per session per state, plus mid-session when the count crosses a
 * threshold") belong to the caller — this component renders what it is handed.
 */
import React, { useEffect } from 'react';
import { Button, ButtonGroup, List } from '@shore/react-gravity';
import { t } from '../../../utils/i18n';
import { CDN_BASE_URL } from '../../../constants';
import { GravityDialog } from '../../../shared/components/GravityDialog';

// Served from the CDN rather than imported: webpack only runs a loader over
// .svg here, and `src/assets` is copied wholesale to the output by
// CopyWebpackPlugin. Same pattern as the down-payments promo banner.
const ARTWORK_SRC = `${CDN_BASE_URL}/images/bookings_upgrade_modal.png`;

const BENEFIT_KEYS = [
  'bookings-upgrade.benefits.unlimited-appointments',
  'bookings-upgrade.benefits.unlimited-products',
  'bookings-upgrade.benefits.no-commission',
];

/** Which of the two the merchant is being shown. */
export type BookingsUpgradeModalState = 'approaching' | 'reached';

export interface BookingsUpgradeModalProps {
  isOpen: boolean;
  state: BookingsUpgradeModalState;
  /** Appointments created against the allowance. */
  used: number;
  /** The allowance itself. */
  limit: number;
  onClose: () => void;
  /** Sends the merchant to checkout. */
  onUpgrade: () => void;
  /**
   * Fired once the dialog is actually on screen.
   *
   * Called from here rather than from whatever decides to open it, because this
   * is the one place that knows it rendered — a caller that counts a dialog it
   * only *asked* for burns the session on one that never appeared.
   */
  onShown?: () => void;
}

export function BookingsUpgradeModal({
  isOpen,
  state,
  used,
  limit,
  onClose,
  onUpgrade,
  onShown,
}: BookingsUpgradeModalProps) {
  useEffect(() => {
    if (isOpen) onShown?.();
  }, [isOpen, onShown]);

  if (!isOpen) return null;

  const isLimitReached = state === 'reached';

  const title = isLimitReached
    ? t('bookings-upgrade.modal.reached.title', { limit })
    : t('bookings-upgrade.modal.approaching.title', { used, limit });

  const description = isLimitReached
    ? t('bookings-upgrade.modal.reached.description')
    : t('bookings-upgrade.modal.approaching.description');

  return (
    <GravityDialog
      isOpen
      modalClassName="gv-upgrade-modal"
      title={title}
      onClose={onClose}
      onClickAway={onClose}
      onEscape={onClose}
      footer={
        <>
          <ButtonGroup>
            <Button variant="cancel" onClick={onClose}>
              {t('bookings-upgrade.modal.close')}
            </Button>
            <Button variant="primary" onClick={onUpgrade}>
              {t('bookings-upgrade.modal.continue-to-payment')}
            </Button>
          </ButtonGroup>

          <div className="gv-modal-image">
            <img src={ARTWORK_SRC} alt="" />
          </div>
        </>
      }
    >
      <p className="gv-product-intro">{description}</p>

      <div className="gv-product-key-benefits">
        <h3 className="gv-modal-subtitle">
          {t('bookings-upgrade.modal.whats-included')}
        </h3>
        <List variant="check">
          {BENEFIT_KEYS.map((key) => (
            <li key={key}>{t(key)}</li>
          ))}
        </List>
      </div>
    </GravityDialog>
  );
}
